import { useEffect, useState, type FormEvent, type KeyboardEvent } from "react";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type AtrasadoDistribucion, type ClaseFiduciante, type Distribucion, type FiduciantePublico } from "@/lib/api";
import {
  aEditable,
  agregarLinea,
  aPayloadDistribucion,
  aTextoApi,
  CIEN,
  claseLibre,
  esValida,
  formatParticipacion,
  formDistribucion,
  hoyIso,
  LINEA_VACIA,
  mensajeRestante,
  mensajeTotales,
  opcionesFiduciantes,
  porcentajeLinea,
  quitarLinea,
  restante,
  sacarLinea,
  separarDistribuciones,
  totalesDistribucion,
  validarDistribucion,
  validarLinea,
  type FormDistribucion,
  type LineaDistribucion,
  type LineaNueva,
  type PayloadDistribucion,
} from "@/lib/participacion";

type Dialogo = { tipo: "form"; distribucion: Distribucion | null } | { tipo: "borrar"; distribucion: Distribucion } | null;

const FORM_VACIO: FormDistribucion = {
  vigente_desde: "",
  nota: "",
  lineas: [],
};

/** Lógica de Participación (sin JSX). Admin y contador la ven; sólo el admin carga, edita y borra. */
export function useParticipacionTab() {
  const { fid, membresia } = useContextoApp();
  const base = `/api/fideicomisos/${fid}`;
  const distribuciones = useApiResource<Distribucion[]>(`${base}/distribuciones`);
  // Acá sólo se usan id y nombre de cada ficha.
  const fiduciantes = useApiResource<FiduciantePublico[]>(`${base}/fiduciantes`);
  const esAdmin = membresia.rol === "admin";
  const lista = distribuciones.data ?? [];
  const { incompletas, vigente, programadas, anteriores } = separarDistribuciones(lista);

  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [form, setForm] = useState<FormDistribucion>(FORM_VACIO);
  // La línea que se está armando y si ya se intentó agregarla (recién ahí se muestran sus errores).
  const [nueva, setNueva] = useState<LineaNueva>(LINEA_VACIA);
  const [intentoAgregar, setIntentoAgregar] = useState(false);

  const accionGuardar = useAsyncAction((id: number | null, datos: PayloadDistribucion) =>
    id === null ? api.post<Distribucion>(`${base}/distribuciones`, datos) : api.put<Distribucion>(`${base}/distribuciones/${id}`, datos),
  );
  const accionBorrar = useAsyncAction((id: number) => api.delete<void>(`${base}/distribuciones/${id}`).then(() => true));

  const listaFiduciantes = fiduciantes.data ?? [];
  const nombreDe = (id: number | null) => listaFiduciantes.find((f) => f.id === id)?.nombre ?? "";
  const erroresNueva = validarLinea(nueva, form);
  // Una línea armada sin tocar Agregar también se guarda (lo que se ve es lo que se guarda).
  const pendiente = nueva.fiduciante_id !== null;
  const formFinal = pendiente && esValida(erroresNueva) ? agregarLinea(form, nueva, nombreDe(nueva.fiduciante_id)) : form;
  const errores = validarDistribucion(formFinal);
  const totales = totalesDistribucion(form);
  const queda = restante(form);
  const recargar = () => {
    distribuciones.recargar();
    fiduciantes.recargar();
  };
  const cerrar = () => setDialogo(null);

  const abrirForm = (nuevoForm: FormDistribucion, distribucion: Distribucion | null) => {
    accionGuardar.limpiarError();
    setForm(nuevoForm);
    setNueva(LINEA_VACIA);
    setIntentoAgregar(false);
    setDialogo({ tipo: "form", distribucion });
  };
  const abrirNueva = () => abrirForm(formDistribucion(vigente, hoyIso()), null);
  const abrirEditar = (d: Distribucion) => abrirForm(formDistribucion(d, d.vigente_desde, d.nota ?? ""), d);

  const cambiarCampo = (campo: "vigente_desde" | "nota", valor: string) => setForm((f) => ({ ...f, [campo]: valor }));

  const elegirFiduciante = (valor: string) => {
    const id = Number(valor);
    setNueva({ fiduciante_id: id, clase: claseLibre(form, id), valor: "" });
    setIntentoAgregar(false);
  };
  const soltarFiduciante = () => setNueva((n) => ({ ...n, fiduciante_id: null }));
  const cambiarClaseNueva = (clase: ClaseFiduciante) => setNueva((n) => ({ ...n, clase }));
  const cambiarValorNueva = (valor: string) => setNueva((n) => ({ ...n, valor }));
  const usarLoQueQueda = () => setNueva((n) => ({ ...n, valor: aEditable(queda) }));

  const agregar = () => {
    setIntentoAgregar(true);
    if (!esValida(erroresNueva)) return;
    setForm(agregarLinea(form, nueva, nombreDe(nueva.fiduciante_id)));
    setNueva(LINEA_VACIA);
    setIntentoAgregar(false);
  };
  /** Enter en el % agrega la línea en vez de mandar el formulario. */
  const teclaEnNueva = (e: KeyboardEvent) => {
    if (e.key !== "Enter" || (e.target as HTMLElement).getAttribute("role") === "combobox") return;
    e.preventDefault();
    agregar();
  };
  const quitar = (indice: number) => setForm((f) => quitarLinea(f, indice));
  const cambiarLinea = (indice: number) => {
    const r = sacarLinea(form, indice);
    setForm(r.form);
    setNueva(r.nueva);
    setIntentoAgregar(false);
  };
  /** "40 %". */
  const textoLinea = (l: LineaDistribucion) => {
    const pct = porcentajeLinea(l);
    return pct === null ? "—" : formatParticipacion(aTextoApi(pct));
  };

  const puedeGuardar = esValida(errores) && !accionGuardar.loading;

  // Lo atrasado que generaría guardarla (Mati, 2026-10-18): lo calcula el backend, con debounce, sólo si suma 100.
  const [atrasado, setAtrasado] = useState<AtrasadoDistribucion | null>(null);
  const editandoId = dialogo?.tipo === "form" ? (dialogo.distribucion?.id ?? null) : null;
  const abiertoForm = dialogo?.tipo === "form";
  const sumaCien = totalesDistribucion(formFinal)?.total === CIEN;
  const payloadPrevia = abiertoForm && sumaCien && esValida(errores) ? JSON.stringify(aPayloadDistribucion(formFinal)) : null;
  useEffect(() => {
    setAtrasado(null);
    if (payloadPrevia === null) return;
    let vigente = true;
    const timer = setTimeout(() => {
      const query = editandoId === null ? "" : `?excepto_id=${editandoId}`;
      api.post<AtrasadoDistribucion>(`${base}/distribuciones/vista-previa${query}`, JSON.parse(payloadPrevia) as PayloadDistribucion)
        .then((r) => vigente && setAtrasado(r))
        .catch(() => undefined); // es una ayuda: si falla, se guarda igual y el listado lo muestra
    }, 300);
    return () => {
      vigente = false;
      clearTimeout(timer);
    };
  }, [payloadPrevia, editandoId, base]);

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    if (dialogo?.tipo !== "form" || !puedeGuardar) return;
    if (pendiente && !esValida(erroresNueva)) {
      setIntentoAgregar(true);
      return;
    }
    if (await accionGuardar.run(dialogo.distribucion?.id ?? null, aPayloadDistribucion(formFinal))) {
      cerrar();
      recargar();
    }
  };

  const abrirBorrar = (d: Distribucion) => {
    accionBorrar.limpiarError();
    setDialogo({ tipo: "borrar", distribucion: d });
  };

  const confirmarBorrar = async () => {
    if (dialogo?.tipo !== "borrar") return;
    if (await accionBorrar.run(dialogo.distribucion.id)) {
      cerrar();
      recargar();
    }
  };

  const faltanDatos = !distribuciones.data || !fiduciantes.data;
  return {
    cargando: faltanDatos && (distribuciones.loading || fiduciantes.loading),
    errorCarga: faltanDatos ? (distribuciones.error?.message ?? fiduciantes.error?.message ?? null) : null,
    reintentar: recargar,
    hayDistribuciones: lista.length > 0,
    incompletas,
    vigente,
    programadas,
    anteriores,
    esAdmin,
    dialogo,
    cerrar,
    form,
    cambiarCampo,
    hayFiduciantes: listaFiduciantes.length > 0,
    opcionesFiduciantes: opcionesFiduciantes(listaFiduciantes, form),
    nueva,
    nombreNueva: nombreDe(nueva.fiduciante_id),
    erroresNueva: intentoAgregar ? erroresNueva : {},
    elegirFiduciante,
    soltarFiduciante,
    cambiarClaseNueva,
    cambiarValorNueva,
    usarLoQueQueda: queda > 0 ? usarLoQueQueda : null,
    textoQueda: queda > 0 ? `Quedan ${formatParticipacion(aTextoApi(queda))} por repartir` : undefined,
    agregar,
    teclaEnNueva,
    quitar,
    cambiarLinea,
    textoLinea,
    errores,
    textoRestante: totales === null ? "Revisá los porcentajes" : mensajeRestante(totales.total),
    textoClases: totales === null ? null : mensajeTotales(totales),
    repartidoTodo: totales?.total === CIEN,
    sumaIncompleta: totales !== null && totales.total > 0 && totales.total < CIEN,
    puedeGuardar,
    atrasadoPrevio: atrasado,
    guardar,
    guardando: accionGuardar.loading,
    errorGuardar: accionGuardar.error,
    abrirNueva,
    abrirEditar,
    abrirBorrar,
    confirmarBorrar,
    borrando: accionBorrar.loading,
    errorBorrar: accionBorrar.error,
  };
}
